import React, { useEffect, useState } from 'react';
import { VStack, Heading, Text, Button, HStack, Box } from '@chakra-ui/react';
import { useSound } from '../../contexts/SoundContext.tsx';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useSocket } from '../../contexts/SocketContext.tsx';
import PlayerName from '../Common/PlayerName.tsx';
import { auth } from '../../config/firebase.ts';

const MotionBox = motion(Box);

interface MultiplayerGameOverProps {
  gameState: any;
  lobbyCode: string;
  onBackToLobby: (lobbyCode?: string) => void;
  onQuit: () => void;
}

const MultiplayerGameOver: React.FC<MultiplayerGameOverProps> = ({ 
  gameState, 
  lobbyCode,
  onBackToLobby,
  onQuit,
}) => {
  const { t } = useTranslation();
  const { playSound } = useSound();
  const { returnToLobby } = useSocket();
  const [isReturning, setIsReturning] = useState(false);
  const winner = gameState.players.find(p => p.id === gameState.winner);

  // Jouer le son de victoire au montage
  useEffect(() => {
    playSound('gameWin');
  }, [playSound]);

  return (
    <MotionBox
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      p={{ base: 4, md: 8 }}
      bg="var(--gris-bleute)"
      borderRadius="xl"
      boxShadow="2xl"
      border="1px solid"
      borderColor="rgba(139, 148, 179, 0.2)"
      w="full"
      maxW="800px"
    >
      <VStack spacing={8}>
        <Heading size="xl" color="var(--texte-clair)">
          {t('gameOverTitle')}
        </Heading>
        
        <Heading size="lg" color="var(--bleu-electrique)">
          🏆 {winner ? t('playerWon', { name: winner.name }) : ''} 🏆
        </Heading>

        <HStack spacing={6} align="stretch">
          {gameState.players.map((player: any) => (
            <VStack 
              key={player.id} 
              p={6} 
              bg="var(--nuit-profonde)" 
              borderRadius="lg" 
              border="1px solid" 
              borderColor="rgba(139, 148, 179, 0.2)"
              flex="1"
            >
              <Text fontSize="xl" fontWeight="bold" color="var(--texte-clair)">
                <PlayerName
                  userId={player.uid || (player.name === auth.currentUser?.displayName ? auth.currentUser?.uid : '') || ''}
                  userName={player.name}
                  checkEffect={!!(player.uid || (player.name === auth.currentUser?.displayName && auth.currentUser?.uid))}
                />
              </Text>
              
              {/* Affichage des points (streak cumulé) */}
              <Text fontSize="lg" fontWeight="bold" color="var(--bleu-electrique)">
                {Object.values(player.score || {}).reduce((total: number, score: any) => total + (Number(score) || 0), 0)} {t('points')}
              </Text>
              
              <Text color="var(--texte-secondaire)">
                {t('validatedCategories')} : {player.completedCategories.filter((catId: string) => {
                    const category = gameState.categories.find((c: any) => c.id === catId);
                    return category ? !category.is_mystery : false;
                }).length} / {gameState.categoriesToWin}
              </Text>
              <Text fontSize="sm" color="var(--texte-secondaire)" textAlign="center" minH="40px">
                {player.completedCategories.map((catId: string) => 
                  gameState.categories.find((c: any) => c.id === catId)?.name
                ).join(', ')}
              </Text>
            </VStack>
          ))}
        </HStack>

        <VStack spacing={4}>
          <HStack spacing={4}>
            <Button
              size="lg"
              onClick={() => {
                if (isReturning) return;
                console.log('[GameOver] Retour au lobby cliqué');
                setIsReturning(true);
                // Émettre l'événement pour que le serveur réinitialise la partie et renvoie tout le monde au lobby
                returnToLobby(lobbyCode);
                // Naviguer immédiatement vers le lobby en optimiste
                try {
                  onBackToLobby?.(lobbyCode);
                } catch (e) {
                  console.warn('Navigation onBackToLobby a échoué, fallback ignoré', e);
                }
              }}
              isLoading={isReturning}
              loadingText={t('backToLobby')}
              className="quizvs-button"
              w="250px"
            >
              {t('backToLobby')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                console.log('[GameOver] Quitter cliqué');
                try { onQuit?.(); } catch {}
              }}
              w="250px"
            >
              {/* Utiliser une chaîne directe pour éviter une clé i18n manquante */}
              Quitter
            </Button>
          </HStack>
        </VStack>
      </VStack>
    </MotionBox>
  );
};

export default MultiplayerGameOver;
